import React, { useState, useRef } from 'react';
import { Tooltip } from './Tooltip';
import { Clock, DollarSign, UserCheck, Trash2 } from 'lucide-react';
import { Switch } from '@headlessui/react';
import { CalculationResult, ROLE_DEFAULTS } from '../types';
import { TaskFields } from './inputs/TaskFields';

interface CalculatorFormProps {
  onCalculate: (result: CalculationResult) => void;
  onDelete?: () => void;
  formIndex: number;
  resultsRef?: React.RefObject<HTMLDivElement>;
  previousValues?: CalculationResult;
}

export function CalculatorForm({ 
  onCalculate, 
  onDelete, 
  formIndex,
  resultsRef,
  previousValues
}: CalculatorFormProps) {
  const formRef = useRef<HTMLDivElement>(null);
  const [role, setRole] = useState('Licensed Users - Blended');
  const [cost, setCost] = useState('57500');
  const [employees, setEmployees] = useState('1000');
  const [ept, setEpt] = useState(2.0);
  const [tasksPerDay, setTasksPerDay] = useState('70');
  const [clicksPerTask, setClicksPerTask] = useState('30');
  const [experience, setExperience] = useState<'Beginner' | 'Seasoned' | 'Expert'>('Seasoned');
  const [isHoursMode, setIsHoursMode] = useState(true);
  const [isFullTime, setIsFullTime] = useState(true);
  const [hoursPerDay, setHoursPerDay] = useState(
    previousValues?.knownExperiencesMode && previousValues?.hoursPerDay 
      ? previousValues.hoursPerDay.toString() 
      : '8'
  );
  const [calculationDate, setCalculationDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalExperiencedLoadsPerDay, setTotalExperiencedLoadsPerDay] = useState('');
  const [knownExperiencesMode, setKnownExperiencesMode] = useState(false);
  const [targetEpt, setTargetEpt] = useState(1.0);

  const handleNumberInput = (value: string, setter: (value: string) => void) => {
    if (value === '' || /^\d+$/.test(value)) {
      setter(value);
    }
  };

  const handleRoleChange = (newRole: string) => {
    setRole(newRole);
    const defaults = ROLE_DEFAULTS[newRole as keyof typeof ROLE_DEFAULTS];
    setTasksPerDay(defaults.tasksPerDay.toString());
    setClicksPerTask(defaults.clicksPerTask.toString());
  };

  const calculateClicksPerMinute = (eptValue: number, exp: 'Beginner' | 'Seasoned' | 'Expert'): number => {
    // Complete Doherty Transactions/minute lookup table (empirical data)
    const dohertyTable = [
      { ept: 0.4, txnsPerMin: 14.70 },
      { ept: 0.5, txnsPerMin: 11.76 },
      { ept: 0.6, txnsPerMin: 9.80 },
      { ept: 0.7, txnsPerMin: 8.40 },
      { ept: 0.8, txnsPerMin: 7.35 },
      { ept: 0.9, txnsPerMin: 6.53 },
      { ept: 1.0, txnsPerMin: 5.88 },
      { ept: 1.1, txnsPerMin: 5.35 },
      { ept: 1.2, txnsPerMin: 4.90 },
      { ept: 1.3, txnsPerMin: 4.52 },
      { ept: 1.4, txnsPerMin: 4.20 },
      { ept: 1.5, txnsPerMin: 3.92 },
      { ept: 1.6, txnsPerMin: 3.68 },
      { ept: 1.7, txnsPerMin: 3.46 },
      { ept: 1.8, txnsPerMin: 3.27 },
      { ept: 1.9, txnsPerMin: 3.09 },
      { ept: 2.0, txnsPerMin: 2.94 },
      { ept: 2.1, txnsPerMin: 2.80 },
      { ept: 2.2, txnsPerMin: 2.67 },
      { ept: 2.3, txnsPerMin: 2.56 },
      { ept: 2.4, txnsPerMin: 2.45 },
      { ept: 2.5, txnsPerMin: 2.35 },
      { ept: 2.6, txnsPerMin: 2.26 },
      { ept: 2.7, txnsPerMin: 2.18 },
      { ept: 2.8, txnsPerMin: 2.10 },
      { ept: 2.9, txnsPerMin: 2.03 },
      { ept: 3.0, txnsPerMin: 1.96 },
      { ept: 3.1, txnsPerMin: 1.90 },
      { ept: 3.2, txnsPerMin: 1.84 },
      { ept: 3.3, txnsPerMin: 1.78 },
      { ept: 3.4, txnsPerMin: 1.73 },
      { ept: 3.5, txnsPerMin: 1.68 },
      { ept: 3.6, txnsPerMin: 1.63 },
      { ept: 3.7, txnsPerMin: 1.59 },
      { ept: 3.8, txnsPerMin: 1.55 },
      { ept: 3.9, txnsPerMin: 1.51 },
      { ept: 4.0, txnsPerMin: 1.47 },
      { ept: 4.1, txnsPerMin: 1.43 },
      { ept: 4.2, txnsPerMin: 1.40 },
      { ept: 4.3, txnsPerMin: 1.37 },
      { ept: 4.4, txnsPerMin: 1.34 },
      { ept: 4.5, txnsPerMin: 1.31 },
      { ept: 4.6, txnsPerMin: 1.28 },
      { ept: 4.7, txnsPerMin: 1.25 },
      { ept: 4.8, txnsPerMin: 1.23 },
      { ept: 4.9, txnsPerMin: 1.20 },
      { ept: 5.0, txnsPerMin: 1.18 },
      { ept: 5.1, txnsPerMin: 1.15 },
      { ept: 5.2, txnsPerMin: 1.13 },
      { ept: 5.3, txnsPerMin: 1.11 },
      { ept: 5.4, txnsPerMin: 1.09 },
      { ept: 5.5, txnsPerMin: 1.07 },
      { ept: 5.6, txnsPerMin: 1.05 },
      { ept: 5.7, txnsPerMin: 1.03 },
      { ept: 5.8, txnsPerMin: 1.01 },
      { ept: 5.9, txnsPerMin: 1.00 },
      { ept: 6.0, txnsPerMin: 0.98 },
    ];

    // Find exact match first
    const exactMatch = dohertyTable.find(point => Math.abs(point.ept - eptValue) < 0.01);
    if (exactMatch) {
      return exactMatch.txnsPerMin;
    }

    // Linear interpolation between two closest points
    const sortedTable = dohertyTable.sort((a, b) => a.ept - b.ept);
    
    for (let i = 0; i < sortedTable.length - 1; i++) {
      const p1 = sortedTable[i];
      const p2 = sortedTable[i + 1];
      
      if (eptValue >= p1.ept && eptValue <= p2.ept) {
        const ratio = (eptValue - p1.ept) / (p2.ept - p1.ept);
        return p1.txnsPerMin + ratio * (p2.txnsPerMin - p1.txnsPerMin);
      }
    }

    // Extrapolation for values outside the range
    if (eptValue < 0.4) return 14.70; // Use max value for very low EPT
    if (eptValue > 6.0) return 0.98; // Use min value for very high EPT
    
    return 0.98; // Default fallback
  };

  const calculateTotalWastedHoursAbsolute = (
    eptValue: number, 
    loadsPerDay: number, 
    exp: 'Beginner' | 'Seasoned' | 'Expert',
    fullTime: boolean,
    targetEpt: number = 0.4
  ): number => {
    const workingDays = fullTime ? 260 : 175;
    const idealEpt = targetEpt; // Use targetEpt parameter, default to 0.4
    
    // Linear Annual Impact: (loads/day) * ((EPT + 1.0s) / 60 / 60) * working days
    const currentLinearDailyHours = loadsPerDay * ((eptValue + 1.0) / 60 / 60);
    const idealLinearDailyHours = loadsPerDay * ((idealEpt + 1.0) / 60 / 60);
    const linearAnnualImpact = Math.max(0, (currentLinearDailyHours - idealLinearDailyHours) * workingDays);
    
    // Exponential Annual Impact: (loads/day) * ((60 / dohertyRate) / 3600) * working days
    const dohertyRateCurrent = calculateClicksPerMinute(eptValue, exp);
    const dohertyRateIdeal = calculateClicksPerMinute(idealEpt, exp);
    const currentExponentialDailyHours = loadsPerDay * ((60 / dohertyRateCurrent) / 3600);
    const idealExponentialDailyHours = loadsPerDay * ((60 / dohertyRateIdeal) / 3600);
    const exponentialAnnualImpact = Math.max(0, (currentExponentialDailyHours - idealExponentialDailyHours) * workingDays);
    
    return linearAnnualImpact + exponentialAnnualImpact;
  };

  const handleCalculate = () => {
    const costNum = Number(cost);
    const workingDaysPerYear = isFullTime ? 250 : 175;
    const standardHoursPerDay = 8;
    const annualHours = standardHoursPerDay * workingDaysPerYear;

    let result: CalculationResult;

    if (knownExperiencesMode && totalExperiencedLoadsPerDay) {
      // Known Experiences Mode Calculations
      const loadsPerDay = Number(totalExperiencedLoadsPerDay);
      const employeesNum = 1; // Set to 1 to remove employee scaling from calculations
      const actualEpt = ept;
      const targetEptValue = targetEpt;
      
      // Calculate Doherty transaction rates for theoretical productivity percentage
      const currentDohertyTxnsPerMin = calculateClicksPerMinute(actualEpt, experience);
      const minDohertyTxnsPerMin = calculateClicksPerMinute(6.0, experience);
      const maxDohertyTxnsPerMin = calculateClicksPerMinute(0.4, experience);
      
      // Calculate rates for actual EPT
      const dohertyRateActual = calculateClicksPerMinute(actualEpt, experience);
      const theoreticalRateActual = 60 / (actualEpt * 2);
      const efficiencyRatioActual = dohertyRateActual / theoreticalRateActual;
      
      // Calculate rates for target EPT
      const dohertyRateTarget = calculateClicksPerMinute(targetEptValue, experience);
      const theoreticalRateTarget = 60 / (targetEptValue * 2);
      const efficiencyRatioTarget = dohertyRateTarget / theoreticalRateTarget;
      
      // Linear impact calculation
      // Formula: (loads/day) * ((EPT + 1.0s human response time) / 60s / 60m) = daily hours
      const linearActualDailyHours = loadsPerDay * ((actualEpt + 1.0) / 60 / 60);
      const linearTargetDailyHours = loadsPerDay * ((targetEptValue + 1.0) / 60 / 60);
      const linearTimeImpactHours = (linearActualDailyHours - linearTargetDailyHours) * 260;
      
      // Exponential impact calculation
      // Formula: (loads/day) * (60 / dohertyRate / 60) = daily hours
      // dohertyRate is txns/min, so 60/dohertyRate gives seconds/txn, then /60 for minutes, /60 for hours
      const exponentialActualDailyHours = loadsPerDay * (60 / dohertyRateActual / 60 / 60);
      const exponentialTargetDailyHours = loadsPerDay * (60 / dohertyRateTarget / 60 / 60);
      const exponentialTimeImpactHours = (exponentialActualDailyHours - exponentialTargetDailyHours) * 260;
      
      // Total company exposure calculations (not per user)
      const actualTotalExposureSecondsPerDay = loadsPerDay * (actualEpt + 1.0);
      const targetTotalExposureSecondsPerDay = loadsPerDay * (targetEptValue + 1.0);
      const wasteTotalSecondsPerDay = actualTotalExposureSecondsPerDay - targetTotalExposureSecondsPerDay;
      
      // Seconds per productive load
      const secondsPerProductiveLoadActual = 60 / dohertyRateActual;
      const secondsPerProductiveLoadTarget = 60 / dohertyRateTarget;
      
      // Daily impact calculations in seconds
      const linearTimeImpactSecondsPerDay = (linearActualDailyHours - linearTargetDailyHours) * 3600;
      const exponentialTimeImpactSecondsPerDay = (exponentialActualDailyHours - exponentialTargetDailyHours) * 3600;
      
      // For known experiences, we calculate productivity as system capacity vs demand
      const systemCapacityLoadsPerDay = dohertyRateActual * Number(hoursPerDay) * 60;

      // Calculate theoretical productivity based on actual EPT vs target EPT
      // At target EPT, productivity should be 100%
      // As EPT increases, productivity decreases
      const theoreticalProductivityPercentage = targetEptValue > 0
        ? Math.min(100, (dohertyRateActual / dohertyRateTarget) * 100)
        : 100;
      const upsideProgressRemainingPercentage = 100 - theoreticalProductivityPercentage;

      // Keep the original productivity calculation for system capacity
      const productivity = Math.min(100, (systemCapacityLoadsPerDay / loadsPerDay) * 100);
      
      const expectedValue = costNum * 1.5;
      const valueProduced = (productivity / 100) * expectedValue;
      const trueCost = valueProduced - expectedValue;
      const totalRoleValue = trueCost * employeesNum;
      
      const expectedHours = Number(hoursPerDay);
      const actualHours = (productivity / 100) * expectedHours;
      const hoursDifference = actualHours - expectedHours;
      const annualHoursDifference = hoursDifference * workingDaysPerYear;

      result = {
        role,
        cost: costNum,
        employees: employeesNum,
        expectedTasks: loadsPerDay,
        tasksPerDay: loadsPerDay,
        maxClicksPerDay: systemCapacityLoadsPerDay,
        theoreticalMaxClicksPerDay: theoreticalRateActual * Number(hoursPerDay) * 60,
        expectedProductivity: loadsPerDay,
        productivity,
        valueProduced,
        expectedValue,
        trueCost,
        totalRoleValue,
        experience,
        isHoursMode,
        hoursPerDay: Number(hoursPerDay),
        hoursDifference,
        annualHoursDifference,
        isFullTime,
        workingDaysPerYear,
        calculationDate,
        ept,
        clicksPerTask: 1, // Each load is 1 "click" in this mode
        totalExperiencedLoadsPerDay: loadsPerDay,
        knownExperiencesMode: true,
        targetEpt: targetEptValue,
        dohertyRateActual,
        theoreticalRateActual,
        efficiencyRatioActual,
        dohertyRateTarget,
        theoreticalRateTarget,
        efficiencyRatioTarget,
        annualLinearImpactHours: linearTimeImpactHours,
        annualExponentialImpactHours: exponentialTimeImpactHours,
        linearTimeImpactSecondsPerDay,
        exponentialTimeImpactSecondsPerDay,
        actualTotalExposureSecondsPerDay,
        targetTotalExposureSecondsPerDay,
        wasteTotalSecondsPerDay,
        secondsPerProductiveLoadActual,
        secondsPerProductiveLoadTarget,
        theoreticalProductivityPercentage,
        upsideProgressRemainingPercentage
      };
    } else {
      // Traditional Mode Calculations
      const clicksPerMinute = calculateClicksPerMinute(ept, experience);
      const maxClicksPerDay = Math.floor(clicksPerMinute * Number(hoursPerDay) * 60);
      const theoreticalMaxRate = 60 / (ept * 2);
      const theoreticalMaxClicksPerDay = Math.floor(theoreticalMaxRate * Number(hoursPerDay) * 60);
      const expectedProductivity = Number(clicksPerTask) * Number(tasksPerDay);
      
      const actualTasksPerDay = Math.floor(maxClicksPerDay / Number(clicksPerTask));
      const expectedTasks = Number(tasksPerDay);
      const productivity = (actualTasksPerDay / expectedTasks) * 100;
      
      const expectedValue = costNum * 1.5;
      const valueProduced = (productivity / 100) * expectedValue;
      const trueCost = valueProduced - expectedValue;
      const totalRoleValue = trueCost * Number(employees);
      
      const expectedHours = Number(hoursPerDay);
      const actualHours = (productivity / 100) * expectedHours;
      const hoursDifference = actualHours - expectedHours;
      const annualHoursDifference = hoursDifference * workingDaysPerYear;
      
      // Calculate Linear Benefit of Improving EPT
      const loadsPerDay = Number(clicksPerTask) * Number(tasksPerDay);
      const linearActualDailyHours = loadsPerDay * ((ept + 1.0) / 60 / 60);
      const linearTargetDailyHours = loadsPerDay * ((1.0 + 1.0) / 60 / 60);
      const linearBenefitHours = Math.max(0, (linearActualDailyHours - linearTargetDailyHours) * 260 * Number(employees));
      
      // Calculate Exponential Potential Benefit of Improving EPT to 1.0s
      const clicksPerMinute_1_0 = calculateClicksPerMinute(1.0, experience);
      const exponentialActualDailyHours = loadsPerDay * ((60 / clicksPerMinute) / 3600);
      const exponentialTargetDailyHours = loadsPerDay * ((60 / clicksPerMinute_1_0) / 3600);
      const exponentialBenefitHours = Math.max(0, (exponentialActualDailyHours - exponentialTargetDailyHours) * 260 * Number(employees));
      
      result = {
        role,
        cost: costNum,
        employees: Number(employees),
        expectedTasks,
        tasksPerDay: expectedTasks,
        maxClicksPerDay,
        theoreticalMaxClicksPerDay,
        expectedProductivity,
        productivity,
        valueProduced,
        expectedValue,
        trueCost,
        totalRoleValue,
        experience,
        isHoursMode,
        hoursPerDay: Number(hoursPerDay),
        hoursDifference,
        annualHoursDifference,
        isFullTime,
        workingDaysPerYear,
        calculationDate,
        ept,
        clicksPerTask: Number(clicksPerTask),
        totalExperiencedLoadsPerDay: undefined,
        knownExperiencesMode: false,
        // Traditional mode benefit calculations
        annualLinearImpactHours: linearBenefitHours,
        annualExponentialImpactHours: exponentialBenefitHours
      };
    }

    onCalculate(result);
  };

  return (
    <div ref={formRef} className="card p-8">
      <div className="flex justify-between items-start mb-4">
        <Tooltip content="Select for a high-traffic record, for example, Case records and lists for Service Agents, or utilize Median/Avg EPT for Blended Licensed Users. Then, if you have prior data or are creating a projection, create another calculator or more than one calculator, to add the dates/data to see a trended chart of data.">
          <h2 className="text-xl font-semibold">Productivity Calculation {formIndex + 1}</h2>
        </Tooltip>
        {onDelete && (
          <button
            onClick={onDelete}
            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-gray-100"
            title="Delete calculator"
          >
            <Trash2 size={20} />
          </button>
        )}
      </div>

      <div className="flex justify-end gap-4 mb-6">
        <Switch.Group>
          <div className="flex items-center gap-2">
            <Switch.Label className="text-sm text-gray-600">
              {isHoursMode ? <Clock size={16} className="inline mr-1" /> : <DollarSign size={16} className="inline mr-1" />}
              {isHoursMode ? 'Hours Mode' : 'Value Mode'}
            </Switch.Label>
            <Switch
              checked={isHoursMode}
              onChange={setIsHoursMode}
              className={`${
                isHoursMode ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  isHoursMode ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>
        </Switch.Group>

        <Switch.Group>
          <div className="flex items-center gap-2">
            <Switch.Label className="text-sm text-gray-600">
              <UserCheck size={16} className="inline mr-1" />
              {isFullTime ? 'Full-Time' : 'Part-Time'}
            </Switch.Label>
            <Switch
              checked={isFullTime}
              onChange={setIsFullTime}
              className={`${
                isFullTime ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  isFullTime ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>
        </Switch.Group>

        <Switch.Group>
          <div className="flex items-center gap-2">
            <Switch.Label className="text-sm text-gray-600">
              Known Experiences
            </Switch.Label>
            <Switch
              checked={knownExperiencesMode}
              onChange={setKnownExperiencesMode}
              className={`${
                knownExperiencesMode ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  knownExperiencesMode ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>
        </Switch.Group>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          <div className="block text-sm font-medium text-gray-700">User Role</div>
          <select
            value={role}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="select-field"
          >
            <option>Licensed Users - Blended</option>
            <option>Service Agent</option>
            <option>Sales Development Rep</option>
            <option>Account Executive</option>
          </select>
        </div>
        
        <div className="space-y-2">
          <Tooltip content="The experience level of employees in this role">
            User Experience
          </Tooltip>
          <select
            value={experience}
            onChange={(e) => setExperience(e.target.value as 'Beginner' | 'Seasoned' | 'Expert')}
            className="select-field"
          >
            <option value="Beginner">Beginner</option>
            <option value="Seasoned">Seasoned</option>
            <option value="Expert">Expert</option>
          </select>
        </div>

        <div className="space-y-2">
          <Tooltip content="Date of calculation">
            Analysis Date
          </Tooltip>
          <input
            type="date"
            value={calculationDate}
            onChange={(e) => setCalculationDate(e.target.value)}
            className="input-field"
          />
        </div>

        {!isHoursMode && (
          <div className="space-y-2">
            <Tooltip content="The estimated wage/year for employees in this role">
              Annual Wage (8hr basis)
            </Tooltip>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={cost}
              onChange={(e) => handleNumberInput(e.target.value, setCost)}
              className="input-field"
            />
          </div>
        )}

        {!knownExperiencesMode && (
          <div className="space-y-2">
            <Tooltip content="Number of employees affected by these errors">
              Number of Users
            </Tooltip>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={employees}
              onChange={(e) => handleNumberInput(e.target.value, setEmployees)}
              className="input-field"
            />
          </div>
        )}

        {knownExperiencesMode ? (
          <div className="space-y-2">
            <Tooltip content="Total number of loads/page views experienced per work day across all users. Use analytics data to determine actual load volume.">
              Total Experienced Loads/Work Day
            </Tooltip>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={totalExperiencedLoadsPerDay}
              onChange={(e) => handleNumberInput(e.target.value, setTotalExperiencedLoadsPerDay)}
              className="input-field"
            />
          </div>
        ) : (
          <TaskFields
            tasksPerDay={tasksPerDay}
            clicksPerTask={clicksPerTask}
            onTasksChange={setTasksPerDay}
            onClicksChange={setClicksPerTask}
          />
        )}

        <div className="space-y-2">
          <Tooltip content="Target EPT for comparison (recommended: 1.0s for optimal productivity)">
            Target EPT (seconds)
          </Tooltip>
          <select
            value={targetEpt}
            onChange={(e) => setTargetEpt(Number(e.target.value))}
            className="select-field"
          >
            {Array.from({ length: 37 }, (_, i) => (4.0 - i * 0.1).toFixed(1))
              .map(Number)
              .filter(val => val >= 0.4)
              .map(val => (
                <option key={val} value={val}>{val.toFixed(1)}</option>
              ))}
          </select>
        </div>

        <div className="space-y-2">
          <Tooltip content={knownExperiencesMode ? "Current EPT being experienced by users" : "Expected Page Time in seconds"}>
            EPT (seconds)
          </Tooltip>
          <select
            value={ept}
            onChange={(e) => setEpt(Number(e.target.value))}
            className="select-field"
          >
            {Array.from({ length: 58 }, (_, i) => (6.0 - i * 0.1).toFixed(1))
              .map(Number)
              .filter(val => val >= 0.4)
              .map(val => (
                <option key={val} value={val}>{val.toFixed(1)}</option>
              ))}
          </select>
        </div>
        
        {knownExperiencesMode && (
          <div className="space-y-2">
            <Tooltip content="Daily work hours for employees using this interface">
              Daily Work Hours
            </Tooltip>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={hoursPerDay}
              onChange={(e) => handleNumberInput(e.target.value, setHoursPerDay)}
              className="input-field"
            />
          </div>
        )}

      </div>

      <button
        onClick={handleCalculate}
        className="btn-primary mt-6"
      >
        Calculate
      </button>
    </div>
  );
}